/*
 * Native search input enhancement — typewriter placeholder animation on
 * #header-search-input. Replaces the equivalent behaviour from ConvertCart
 * IntelliSearch (see Dev/cc-intellisearch-replacement/, PR-B1). The theme's
 * <search-form> custom element handles all the actual predictive search
 * logic — this file only adds the placeholder animation on top of it.
 *
 * Behaviour:
 *   - Cycles through a list of terms, typing each one letter-by-letter
 *     (~120ms/char), pausing at the full word (~2s), then next term.
 *   - Runs only while the input is EMPTY and NOT FOCUSED. Any user
 *     interaction (focus, keydown, value change) instantly pauses.
 *   - Resumes when the user blurs with an empty input.
 *   - Static placeholder text is preserved from the input's own attribute
 *     as a fallback if the animation is disabled or paused for a long time.
 *
 * Term list is hardcoded here for now (matches CC's 6 curated terms). Follow-up
 * PR should promote this to a theme customiser setting so Benj can edit.
 *
 * Vanilla JS, ES5, no dependencies. IIFE.
 */
(function () {
  'use strict';

  var TERMS = [
    'jojoba oil',
    'shea butter',
    'vitamin e',
    'preservative eco',
    'hyaluronic acid',
    'vitamin e oil'
  ];
  var CHAR_MS = 120;      // ms per character while typing
  var HOLD_MS = 2000;     // ms to hold on full word before typing next
  var BLANK_MS = 400;     // ms to hold on empty before starting next term

  function init() {
    var input = document.getElementById('header-search-input');
    if (!input) return;

    var originalPlaceholder = input.getAttribute('placeholder') || '';
    var termIndex = 0;
    var charIndex = 0;
    var isTyping = true;       // typing forward vs holding
    var timer = null;
    var paused = false;

    function stop() {
      if (timer) { clearTimeout(timer); timer = null; }
    }

    function pause() {
      paused = true;
      stop();
      // Restore the fallback placeholder so if animation stays paused,
      // the input doesn't sit with a partial word.
      input.setAttribute('placeholder', originalPlaceholder);
    }

    function resume() {
      if (!paused) return;
      paused = false;
      // Reset to a clean start of the current term so the animation is coherent.
      charIndex = 0;
      isTyping = true;
      schedule(BLANK_MS);
    }

    function shouldRun() {
      // Only animate when the input is empty AND the user isn't interacting.
      // document.activeElement covers focus; input.value covers a typed query.
      return !input.value && document.activeElement !== input && !paused;
    }

    function tick() {
      if (!shouldRun()) return;
      var term = TERMS[termIndex] || '';

      if (isTyping) {
        charIndex += 1;
        input.setAttribute('placeholder', ' ' + term.slice(0, charIndex));
        if (charIndex >= term.length) {
          isTyping = false;
          schedule(HOLD_MS);
        } else {
          schedule(CHAR_MS);
        }
      } else {
        // After the hold, blank out and advance to the next term.
        input.setAttribute('placeholder', originalPlaceholder);
        isTyping = true;
        charIndex = 0;
        termIndex = (termIndex + 1) % TERMS.length;
        schedule(BLANK_MS);
      }
    }

    function schedule(ms) {
      stop();
      timer = setTimeout(tick, ms);
    }

    // Pause on any user interaction — preserves whatever they've typed and
    // prevents the animation from clobbering their input in flight.
    input.addEventListener('focus', pause);
    input.addEventListener('input', pause);
    input.addEventListener('blur', function () {
      // Small delay so predictive-search dropdown blur (if any) settles first.
      setTimeout(function () {
        if (!input.value) resume();
      }, 150);
    });

    // Also pause on page visibility change — animating in a background tab
    // is wasted CPU.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else if (!input.value && document.activeElement !== input) {
        charIndex = 0;
        isTyping = true;
        schedule(BLANK_MS);
      }
    });

    // Kick off after a short delay so the page has time to settle and the
    // user isn't hit with an animation mid-load.
    schedule(1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // -------------------------------------------------------------------------
  // Recent Searches populator — reads AroSearchAPI.getRecentSearches() and
  // renders a plain link list (no chips) into any [data-aro-recent-list]
  // container on the page (header dropdown + /search sidebar both use this).
  // Whole [data-aro-recent-section] wrapper hides when the list is empty.
  // -------------------------------------------------------------------------

  // Display cap: top N shown in the Recent Searches UI. Storage cap
  // (RECENT_MAX in native-search-api.js) is larger — see comment there.
  // Design intent per HANDOFF: "top 6 shown, up to 10 stored".
  var RECENT_LIMIT = 6;
  var SEARCH_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>';

  function escHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderRecentSearches() {
    var containers = document.querySelectorAll('[data-aro-recent-list]');
    if (!containers.length) return;
    var api = window.AroSearchAPI;
    var list = (api && typeof api.getRecentSearches === 'function') ? api.getRecentSearches() : [];
    var top = list.slice(0, RECENT_LIMIT);
    containers.forEach(function (container) {
      var section = container.closest('[data-aro-recent-section]');
      if (top.length === 0) {
        if (section) section.setAttribute('hidden', '');
        container.innerHTML = '';
        return;
      }
      if (section) section.removeAttribute('hidden');
      container.innerHTML = top.map(function (term) {
        var enc = encodeURIComponent(term);
        var safe = escHtml(term);
        return '<a href="/search?q=' + enc + '" title="' + safe + '" class="aro-search-focus__popular-item">' + SEARCH_ICON_SVG + '<span>' + safe + '</span></a>';
      }).join('');
    });
  }

  function initRecent() {
    renderRecentSearches();
    var input = document.getElementById('header-search-input');
    if (input) {
      // Re-render on focus so if another tab added a query since page load,
      // the list stays current.
      input.addEventListener('focus', renderRecentSearches);
    }
    // Cross-tab sync via storage event.
    window.addEventListener('storage', function (e) {
      if (e.key === 'aro_recent_searches') renderRecentSearches();
    });
    // Same-tab notification — /search page bootstrap saves the current
    // query AFTER our initial render, so we need a signal to re-read.
    window.addEventListener('aro:recent-updated', renderRecentSearches);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRecent);
  } else {
    initRecent();
  }

  // Warm all product images inside the search dropdown — both the focus-state
  // Best Sellers grid AND the typing-state Products/Blogs/Recipes tabs — so
  // the first user interaction doesn't show blank tiles briefly.
  //
  // Root cause: the dropdown container (.thb-predictive-search) is display:
  // none until the user focuses the header search input, and browsers skip
  // fetching loading="lazy" images inside display:none containers. First
  // focus / first type triggers image fetches that resolve visibly AFTER the
  // popup shows. Switching lazy → eager with fetchPriority=low pre-fetches
  // in the background without competing with above-the-fold LCP images. Once
  // cached, opens are instant.
  //
  // Two-stage coverage:
  //   1. On DOMContentLoaded: warm the server-rendered Best Sellers grid.
  //   2. MutationObserver on the predictive-search container: warm images in
  //      any DOM subtree added later (typing-state results from the theme's
  //      <search-form> fetch, JS-rendered Blogs/Recipes panels, filter re-
  //      renders). Idempotent — imgs already eager are skipped by the
  //      [loading="lazy"] selector.
  function warmDropdownImages(root) {
    var scope = root || document;
    var imgs = scope.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < imgs.length; i++) {
      // Only warm images INSIDE the dropdown — other lazy imgs on the page
      // (product listings, PDPs, blog articles) should stay lazy for perf.
      if (imgs[i].closest('.thb-predictive-search, .aro-search-focus, .aro-search-dropdown')) {
        imgs[i].loading = 'eager';
        try { imgs[i].fetchPriority = 'low'; } catch (e) { /* older browsers */ }
      }
    }
  }
  function setupDropdownWarmObserver() {
    if (typeof MutationObserver === 'undefined') return;
    var container = document.querySelector('.thb-predictive-search');
    if (!container) return;
    var observer = new MutationObserver(function () {
      warmDropdownImages(container);
    });
    observer.observe(container, { childList: true, subtree: true });
  }
  function initWarming() {
    warmDropdownImages();
    setupDropdownWarmObserver();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWarming);
  } else {
    initWarming();
  }
})();
